import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Table,
  Modal,
  Typography,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import API from "../../api";
import { useSelector } from "react-redux";

const { Option } = Select;
const { Title } = Typography;

const PurchaseDrawer = ({ open, setOpen, data, onSave }) => {
  const [form] = Form.useForm();
  const [taxForm] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [productsInPurchase, setProductsInPurchase] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [taxModal, setTaxModal] = useState(false);
  const [taxAmount, setTaxAmount] = useState(null);
  const [paidAmount, setPaidAmount] = useState("0.00");
  const [balanceAmount, setBalanceAmount] = useState("0.00");
  const [isPaidAmountEdited, setIsPaidAmountEdited] = useState(false);
  const [purchaseDiscount, setPurchaseDiscount] = useState({
    value: 0,
    type: "percentage",
  });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [purchaseFormData, setPurchaseFormData] = useState(null);

  const productSettings = useSelector(
    (state) => state.productSetting?.productSetting
  );

  const [storeSettings, setStoreSettings] = useState({
    trackingNumber: "No",
    batchNumber: "No",
  });

  const lineItemIdRef = useRef(0);

  const fetchUser = useCallback(() => {
    const userData = JSON.parse(localStorage?.getItem("userData"));
    if (userData) {
      if (userData.role === "admin") {
        const settings = productSettings.find(
          (setting) => setting.storeId === data?.storeId?._id
        );
        if (settings) {
          setStoreSettings({
            trackingNumber: settings?.engineNumber || "No",
            batchNumber: settings?.batchNumber || "No",
          });
        } else {
          setStoreSettings({ trackingNumber: "No", batchNumber: "No" });
        }
      } else if (userData.storeId) {
        const settings = productSettings.find(
          (setting) => setting.storeId === userData.storeId
        );
        if (settings) {
          setStoreSettings({
            trackingNumber: settings?.engineNumber || "No",
            batchNumber: settings?.batchNumber || "No",
          });
        }
      }
    }
  }, [data?.storeId, productSettings]);

  const fetchStoreById = async (id) => {
    try {
      const response = await API.get(`/stores/${id}`);
      setStores([response.data]);
    } catch (error) {
      console.error("Error fetching store:", error);
      message.error("Failed to fetch store.");
    }
  };

  const fetchSupplierById = async (id) => {
    try {
      const response = await API.get(`/suppliers/${id}`);
      setSuppliers([response.data]);
    } catch (error) {
      console.error("Error fetching supplier:", error);
      message.error("Failed to fetch supplier.");
    }
  };

  const fetchTaxes = async () => {
    try {
      const taxResponse = await API.get("/taxes");
      setTaxes(taxResponse?.data);
    } catch (error) {
      console.error("Error fetching taxes:", error);
      message.error("Failed to fetch taxes.");
    }
  };

  useEffect(() => {
    if (data) {
      fetchStoreById(data.storeId?._id);
      fetchSupplierById(data.supplierId?._id);
      fetchTaxes();
      fetchUser();
    }
  }, [data, fetchUser]);

  useEffect(() => {
    if (data) {
      const initialPaidAmount = data.paidAmount ? Number(data.paidAmount) : 0;
      const totalAmount = Number(data.totalAmount) || 0;
      const calculatedBalance = totalAmount - initialPaidAmount;

      form.setFieldsValue({
        storeId: data.storeId?._id,
        supplierId: data.supplierId?._id,
        invoiceId: data.invoiceId,
        orderDate: moment(data.orderDate),
        status: data.status,
        paidAmount: initialPaidAmount.toFixed(2),
        balanceAmount: calculatedBalance.toFixed(2),
        paymentMethod: data.paymentMethod || null,
      });

      setProductsInPurchase(
        data.products.map((product) => ({
          id: lineItemIdRef.current++,
          productId: product.productId,
          productName: product.productId?.specifications?.name,
          quantity: product.quantity,
          totalPrice: parseFloat(product.totalPrice || 0).toFixed(2),
          hsn_sac: product.hsn_sac || "",
          mrpPrice: parseFloat(product.mrpPrice || 0).toFixed(2),
          salePrice: parseFloat(product.salePrice || 0).toFixed(2),
          purchasePrice: parseFloat(product.purchasePrice || 0).toFixed(2),
          batchNumber: product.batchNumber || "",
          taxes: product.taxes[0] || null,
          productDiscount: product.productDiscount || {
            value: 0,
            type: "percentage",
          },
        }))
      );
      setPaidAmount(initialPaidAmount.toFixed(2));
      setBalanceAmount(calculatedBalance.toFixed(2));
      setPurchaseDiscount(data.discount || { value: 0, type: "percentage" });
      setPaymentMethod(data.paymentMethod || null);
    } else {
      form.resetFields();
      setProductsInPurchase([]);
      setPaidAmount("0.00");
      setBalanceAmount("0.00");
      setPurchaseDiscount({ value: 0, type: "percentage" });
      setPaymentMethod(null);
    }
  }, [data, form]);

  const handlePaidAmountChange = (value) => {
    const newPaidAmount =
      value === null || value === "" ? "0.00" : parseFloat(value).toFixed(2);
    setPaidAmount(newPaidAmount);
    setIsPaidAmountEdited(true);

    const { totalAmount } = calculateSummary();
    setBalanceAmount(
      (parseFloat(totalAmount) - parseFloat(newPaidAmount)).toFixed(2)
    );
  };

  const handleQuantityChange = (id, quantity) => {
    if (
      quantity === null ||
      quantity < 0 ||
      !Number.isInteger(Number(quantity))
    ) {
      message.error(
        "Quantity must be a whole number greater than or equal to 0."
      );
      return;
    }
    setProductsInPurchase((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              quantity: quantity,
              totalPrice: applyProductDiscount(
                item,
                parseFloat(quantity) * parseFloat(item.purchasePrice)
              ),
            }
          : item
      )
    );
  };

  const handlePurchasePriceChange = (id, newPurchasePrice) => {
    if (newPurchasePrice === null || newPurchasePrice < 0) return;
    setProductsInPurchase((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              purchasePrice: parseFloat(newPurchasePrice).toFixed(2),
              totalPrice: applyProductDiscount(
                item,
                parseFloat(item.quantity) * parseFloat(newPurchasePrice)
              ),
            }
          : item
      )
    );
  };

  const handleMrpPriceChange = (id, newMrpPrice) => {
    if (newMrpPrice === null || newMrpPrice < 0) return;
    setProductsInPurchase((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              mrpPrice: parseFloat(newMrpPrice).toFixed(2),
            }
          : item
      )
    );
  };

  const handleSalePriceChange = (id, newSalePrice) => {
    if (newSalePrice === null || newSalePrice < 0) return;
    setProductsInPurchase((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              salePrice: parseFloat(newSalePrice).toFixed(2),
            }
          : item
      )
    );
  };

  const handleDiscountChange = (id, discountType, discountValue) => {
    if (discountValue === null || discountValue < 0) return;

    setProductsInPurchase((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const totalBasePrice =
            parseFloat(item.quantity) * parseFloat(item.purchasePrice);
          if (discountType === "percentage" && discountValue > 100) {
            message.error("Percentage discount cannot exceed 100%.");
            return item;
          }
          if (discountType === "flat" && discountValue > totalBasePrice) {
            message.error("Flat discount cannot exceed total price.");
            return item;
          }

          let totalPrice = applyProductDiscount(
            item,
            totalBasePrice,
            discountType,
            discountValue,
            item.taxes?.percentage || 0
          );

          return {
            ...item,
            totalPrice: totalPrice,
            productDiscount: {
              type: discountType,
              value: parseFloat(discountValue).toFixed(2),
            },
          };
        }
        return item;
      })
    );
  };

  const handleBatchNumberChange = (id, batchNumber) => {
    setProductsInPurchase((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              batchNumber: batchNumber || "",
            }
          : item
      )
    );
  };

  const applyProductDiscount = (
    item,
    totalPrice,
    discountType = item.productDiscount?.type,
    discountValue = item.productDiscount?.value,
    taxPercentage = item.taxes?.percentage || 0
  ) => {
    if (discountType === "percentage" && discountValue) {
      totalPrice -= (parseFloat(discountValue) / 100) * totalPrice;
    } else if (discountType === "flat" && discountValue) {
      totalPrice -= parseFloat(discountValue);
    }
    totalPrice += (parseFloat(taxPercentage) / 100) * totalPrice;
    return parseFloat(totalPrice.toFixed(2));
  };

  const calculateSummary = () => {
    let netTotal = 0,
      totalDiscount = 0,
      taxableAmount = 0;

    productsInPurchase.forEach((item) => {
      const total = parseFloat(item.quantity) * parseFloat(item.purchasePrice);
      const discount =
        item.productDiscount?.type === "percentage"
          ? (parseFloat(item.productDiscount.value) / 100) * total
          : parseFloat(item.productDiscount?.value) || 0;

      const tax = item.taxes?.percentage
        ? ((total - discount) * parseFloat(item.taxes.percentage)) / 100
        : 0;

      taxableAmount += tax;
      netTotal += total;
      totalDiscount += discount;
    });

    const purchaseLevelDiscount =
      purchaseDiscount.type === "percentage"
        ? (parseFloat(purchaseDiscount.value) / 100) *
          (netTotal - totalDiscount)
        : parseFloat(purchaseDiscount.value) || 0;

    const totalAmount =
      netTotal - totalDiscount - purchaseLevelDiscount + taxableAmount;
    setTaxAmount(taxableAmount.toFixed(2));
    if (!isPaidAmountEdited) {
      setBalanceAmount(totalAmount.toFixed(2));
    } else {
      setBalanceAmount((totalAmount - parseFloat(paidAmount)).toFixed(2));
    }

    return {
      netTotal: netTotal.toFixed(2),
      totalDiscount: (totalDiscount + purchaseLevelDiscount).toFixed(2),
      taxableAmount: taxableAmount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
    };
  };

  const formatNumber = (number) => {
    return `₹${new Intl.NumberFormat("en-IN").format(parseFloat(number))}`;
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const { taxableAmount, totalAmount } = calculateSummary();

    if (values.status === "completed" && !paymentMethod) {
      message.error("Payment method is required for completed purchases.");
      setLoading(false);
      return;
    }

    if (productsInPurchase.some((item) => !item.taxes)) {
      message.error("Please select a tax for all products in the purchase.");
      setLoading(false);
      return;
    }

    try {
      const purchaseData = {
        ...values,
        storeId: form.getFieldValue("storeId"),
        paidAmount: parseFloat(paidAmount).toFixed(2),
        paymentMethod: paymentMethod,
        balanceAmount: (totalAmount - parseFloat(paidAmount)).toFixed(2),
        products: productsInPurchase.map((item) => ({
          productId: item.productId,
          hsn_sac: item.hsn_sac,
          mrpPrice: parseFloat(item.mrpPrice).toFixed(2),
          quantity: Number(item.quantity),
          productDiscount: item.productDiscount,
          salePrice: parseFloat(item.salePrice).toFixed(2),
          totalPrice: parseFloat(item.totalPrice).toFixed(2),
          purchasePrice: parseFloat(item.purchasePrice).toFixed(2),
          batchNumber: item.batchNumber || "",
          gst: item.taxes
            ? { id: item.taxes._id, percentage: item.taxes.percentage }
            : { id: null, percentage: 0 },
          taxes: item.taxes
            ? [
                {
                  taxId: item.taxes._id,
                  taxName: item.taxes.taxName,
                  percentage: item.taxes.percentage,
                },
              ]
            : [],
        })),
        discount: purchaseDiscount,
        totalAmountWithoutTax: (totalAmount - taxableAmount).toFixed(2),
        totalAmount: totalAmount,
      };

      setPurchaseFormData(purchaseData);
      setShowConfirmationModal(true);
    } catch (error) {
      console.error("Error preparing purchase data:", error);
      message.error("Failed to prepare purchase data.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPurchase = async () => {
    if (!purchaseFormData) return;

    setLoading(true);
    try {
      const res = await API.put(`/purchase/${data?._id}`, purchaseFormData);
      if (res.status === 200) {
        message.success("Purchase updated successfully!");
        setShowConfirmationModal(false);
        setPurchaseFormData(null);
        onSave();
        setOpen(false);
      }
    } catch (error) {
      console.error("Error updating purchase:", error);
      message.error(
        error?.response?.data?.message || "Failed to update purchase."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddTax = async (values) => {
    try {
      const res = await API.post("/taxes", values);
      if (res.status === 200) {
        message.success("Tax Added Successfully.");
        setTaxModal(false);
        fetchTaxes();
      }
    } catch (error) {
      message.error(error?.response?.data?.message);
    }
  };

  const columns = [
    {
      title: "Product Description",
      dataIndex: "productName",
      key: "productName",
    },
    ...(storeSettings.batchNumber === "Yes"
      ? [
          {
            title: "Batch No",
            dataIndex: "batchNumber",
            key: "batchNumber",
            render: (_, record) => (
              <Input
                value={record.batchNumber}
                onChange={(e) =>
                  handleBatchNumberChange(record.id, e.target.value)
                }
                placeholder="Enter Batch No"
              />
            ),
          },
        ]
      : []),
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (_, record) => (
        <InputNumber
          min={0}
          step={1}
          value={record.quantity}
          onChange={(value) => handleQuantityChange(record.id, value)}
          precision={0}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, record) => (
        <Space>
          <InputNumber
            addonAfter={
              <Select
                value={record.productDiscount?.type || "percentage"}
                onChange={(value) =>
                  handleDiscountChange(
                    record.id,
                    value,
                    record.productDiscount?.value || 0
                  )
                }
              >
                <Option value="percentage">%</Option>
                <Option value="flat">Flat</Option>
              </Select>
            }
            min={0}
            step={0.01}
            precision={2}
            placeholder="Discount"
            value={record.productDiscount?.value}
            onChange={(value) =>
              handleDiscountChange(
                record.id,
                record.productDiscount?.type || "percentage",
                value
              )
            }
            style={{ width: "150px" }}
          />
        </Space>
      ),
    },
    {
      title: "Purchase Price",
      dataIndex: "purchasePrice",
      key: "purchasePrice",
      render: (_, record) => (
        <InputNumber
          min={0}
          step={0.01}
          value={record.purchasePrice}
          onChange={(value) => handlePurchasePriceChange(record.id, value)}
          precision={2}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "MRP",
      dataIndex: "mrpPrice",
      key: "mrpPrice",
      render: (_, record) => (
        <InputNumber
          min={0}
          step={0.01}
          value={record.mrpPrice}
          onChange={(value) => handleMrpPriceChange(record.id, value)}
          precision={2}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Sale Price",
      dataIndex: "salePrice",
      key: "salePrice",
      render: (_, record) => (
        <InputNumber
          min={0}
          step={0.01}
          value={record.salePrice}
          onChange={(value) => handleSalePriceChange(record.id, value)}
          precision={2}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Tax</span>
          <Button
            type="primary"
            shape="circle"
            icon={<PlusOutlined />}
            size="small"
            onClick={() => setTaxModal(true)}
            style={{ marginLeft: "8px" }}
          />
        </div>
      ),
      key: "taxes",
      render: (_, record) => (
        <Select
          style={{ minWidth: 100 }}
          placeholder="Select Tax"
          value={
            record.taxes
              ? `${record.taxes.taxName} (${record.taxes.percentage}%)`
              : null
          }
          onChange={(value) => {
            const selectedTax = taxes.find(
              (tax) => `${tax.taxName} (${tax.percentage}%)` === value
            );
            setProductsInPurchase((prev) =>
              prev.map((item) =>
                item.id === record.id
                  ? {
                      ...item,
                      taxes: selectedTax || null,
                      totalPrice: applyProductDiscount(
                        item,
                        parseFloat(item.purchasePrice) *
                          parseFloat(item.quantity),
                        item.productDiscount?.type,
                        item.productDiscount?.value,
                        selectedTax?.percentage || 0
                      ),
                    }
                  : item
              )
            );
          }}
          status={!record.taxes ? "error" : ""}
        >
          {taxes.length === 0 ? (
            <Option disabled>No Tax available</Option>
          ) : (
            taxes.map((tax) => (
              <Option
                key={tax._id}
                value={`${tax.taxName} (${tax.percentage}%)`}
              >
                {`${tax.taxName} (${tax.percentage}%)`}
              </Option>
            ))
          )}
        </Select>
      ),
    },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      key: "totalPrice",
      render: (value) => formatNumber(value),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button danger icon={<DeleteOutlined />} disabled={true} />
      ),
    },
  ];

  return (
    <Drawer
      title="Edit Purchase"
      width={1200}
      onClose={() => setOpen(false)}
      open={open}
      styles={{
        body: {
          padding: "20px",
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        },
      }}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={16}>
          <Col xs={24} sm={6}>
            <Form.Item
              name="storeId"
              label="Select Store"
              rules={[{ required: true, message: "Please select a store." }]}
            >
              <Select
                placeholder="Select Store"
                disabled={true}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.props?.children
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {stores.map((store) => (
                  <Option key={store?._id} value={store?._id}>
                    {store?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="supplierId"
              label="Supplier"
              rules={[{ required: true, message: "Please select a supplier." }]}
            >
              <Select
                placeholder="Select Supplier"
                disabled={true}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.props?.children
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {suppliers.map((supplier) => (
                  <Option key={supplier?._id} value={supplier?._id}>
                    {supplier?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="invoiceId"
              label="Invoice Number"
              rules={[
                { required: true, message: "Please enter the invoice number." },
              ]}
            >
              <Input placeholder="Enter Invoice Number" disabled={true} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="orderDate"
              label="Invoice Date"
              rules={[
                { required: true, message: "Please select an invoice date." },
              ]}
            >
              <DatePicker
                placeholder="Select Date"
                style={{ width: "100%" }}
                disabled={true}
              />
            </Form.Item>
          </Col>
        </Row>

        <Table
          style={{ marginTop: "12px" }}
          columns={columns}
          dataSource={productsInPurchase}
          rowKey="id"
          pagination={false}
          footer={() => {
            const { taxableAmount, netTotal, totalDiscount, totalAmount } =
              calculateSummary();
            const netTotalNum = parseFloat(netTotal) || 0;

            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  marginTop: "16px",
                  borderTop: "2px solid #e8e8e8",
                  padding: "16px 0",
                  backgroundColor: "#fafafa",
                  borderRadius: "8px",
                }}
              >
                <Row gutter={[24, 16]} align="top">
                  <Col xs={24} lg={16}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                        padding: "20px",
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <div>
                        <h4
                          style={{
                            margin: "0 0 12px 0",
                            color: "#1890ff",
                            fontSize: "16px",
                            fontWeight: "600",
                            borderBottom: "1px solid #e8e8e8",
                            paddingBottom: "8px",
                          }}
                        >
                          Purchase Discount & Payment
                        </h4>
                        <Row gutter={[16, 16]} align="middle">
                          <Col xs={24} sm={12} md={8}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#595959",
                                marginBottom: "8px",
                              }}
                            >
                              Purchase Discount
                            </label>
                            <InputNumber
                              addonAfter={
                                <Select
                                  value={purchaseDiscount.type}
                                  onChange={(value) =>
                                    setPurchaseDiscount((prev) => ({
                                      ...prev,
                                      type: value,
                                    }))
                                  }
                                >
                                  <Option value="percentage">%</Option>
                                  <Option value="flat">Flat</Option>
                                </Select>
                              }
                              min={0}
                              step={0.01}
                              precision={2}
                              placeholder="Purchase Discount"
                              value={purchaseDiscount.value}
                              onChange={(value) => {
                                if (value === null || value < 0) return;
                                const type = purchaseDiscount.type;
                                if (type === "percentage" && value > 100) {
                                  message.error(
                                    "Percentage discount cannot exceed 100%."
                                  );
                                  return;
                                }
                                if (type === "flat" && value > netTotalNum) {
                                  message.error(
                                    "Flat discount cannot exceed the total amount."
                                  );
                                  return;
                                }
                                setPurchaseDiscount((prev) => ({
                                  ...prev,
                                  value: parseFloat(value).toFixed(2),
                                }));
                              }}
                              style={{
                                width: "100%",
                                borderRadius: "6px",
                                borderColor: "#d9d9d9",
                              }}
                            />
                          </Col>
                          <Col xs={24} sm={12} md={8}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#595959",
                                marginBottom: "8px",
                              }}
                            >
                              Paid Amount
                            </label>
                            <InputNumber
                              min={0}
                              step={0.01}
                              precision={2}
                              value={paidAmount}
                              onChange={handlePaidAmountChange}
                              style={{
                                width: "100%",
                                borderRadius: "6px",
                                borderColor: "#d9d9d9",
                              }}
                            />
                          </Col>
                          <Col xs={24} sm={12} md={8}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#595959",
                                marginBottom: "8px",
                              }}
                            >
                              Balance Amount
                            </label>
                            <InputNumber
                              value={balanceAmount}
                              readOnly
                              precision={2}
                              style={{
                                width: "100%",
                                borderRadius: "6px",
                                borderColor: "#d9d9d9",
                                backgroundColor: "#f5f5f5",
                                color: "#8c8c8c",
                              }}
                            />
                          </Col>
                        </Row>
                        <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item
                              name="status"
                              rules={[{ required: true }]}
                              label={
                                <label
                                  style={{
                                    display: "block",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#595959",
                                    marginBottom: "8px",
                                  }}
                                >
                                  Status
                                </label>
                              }
                            >
                              <Select placeholder="Select Status" showSearch>
                                {data?.status === "pending"
                                  ? ["completed", "cancelled"].map((status) => (
                                      <Option key={status} value={status}>
                                        {status.charAt(0).toUpperCase() +
                                          status.slice(1)}
                                      </Option>
                                    ))
                                  : ["pending", "completed", "cancelled"].map(
                                      (status) => (
                                        <Option key={status} value={status}>
                                          {status.charAt(0).toUpperCase() +
                                            status.slice(1)}
                                        </Option>
                                      )
                                    )}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={16}>
                            <Form.Item
                              name="paymentMethod"
                              rules={[{ required: true }]}
                              label={
                                <label
                                  style={{
                                    display: "block",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#595959",
                                    marginBottom: "8px",
                                  }}
                                >
                                  Payment Method{" "}
                                  <span style={{ color: "#ff4d4f" }}>*</span>
                                </label>
                              }
                            >
                              <Select
                                required
                                placeholder="Payment Method"
                                value={paymentMethod}
                                onChange={(value) => setPaymentMethod(value)}
                                style={{
                                  width: "100%",
                                  borderRadius: "6px",
                                  borderColor:
                                    form.getFieldValue("status") ===
                                      "completed" && !paymentMethod
                                      ? "#ff4d4f"
                                      : "#d9d9d9",
                                }}
                                status={
                                  form.getFieldValue("status") ===
                                    "completed" && !paymentMethod
                                    ? "error"
                                    : ""
                                }
                                options={[
                                  { value: "cash", label: "Cash" },
                                  { value: "upi", label: "UPI" },
                                  {
                                    value: "credit_card",
                                    label: "Credit Card",
                                  },
                                  { value: "debit_card", label: "Debit Card" },
                                  {
                                    value: "bank_transfer",
                                    label: "Bank Transfer",
                                  },
                                  { value: "other", label: "Other" },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} lg={8}>
                    <div
                      style={{
                        padding: "20px",
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                        height: "fit-content",
                        position: "sticky",
                        top: "20px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#595959",
                              fontWeight: "500",
                            }}
                          >
                            Net Total
                          </span>
                          <span
                            style={{
                              fontSize: "16px",
                              color: "#ff4d4f",
                              fontWeight: "600",
                            }}
                          >
                            {formatNumber(netTotal)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#595959",
                              fontWeight: "500",
                            }}
                          >
                            Discount
                          </span>
                          <span style={{ fontSize: "16px", color: "#595959" }}>
                            {formatNumber(totalDiscount)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#595959",
                              fontWeight: "500",
                            }}
                          >
                            Tax
                          </span>
                          <span style={{ fontSize: "16px", color: "#595959" }}>
                            {formatNumber(taxableAmount)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#595959",
                              fontWeight: "500",
                            }}
                          >
                            Payable (Before Tax)
                          </span>
                          <span
                            style={{
                              fontSize: "16px",
                              color: "#52c41a",
                              fontWeight: "600",
                            }}
                          >
                            {formatNumber(
                              (netTotal - totalDiscount).toFixed(2)
                            )}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 0",
                            borderTop: "2px solid #1890ff",
                            marginTop: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "16px",
                              color: "#1890ff",
                              fontWeight: "600",
                            }}
                          >
                            Total Payable
                          </span>
                          <span
                            style={{
                              fontSize: "20px",
                              color: "#1890ff",
                              fontWeight: "700",
                            }}
                          >
                            {formatNumber(totalAmount)}
                          </span>
                        </div>
                      </div>
                      <Form.Item style={{ marginTop: "20px", marginBottom: 0 }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={isLoading}
                          block
                          style={{
                            height: "48px",
                            fontSize: "16px",
                            fontWeight: "600",
                            borderRadius: "8px",
                          }}
                        >
                          Update Purchase
                        </Button>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </div>
            );
          }}
        />
      </Form>

      {taxModal && (
        <Modal
          title="Add Tax"
          open={taxModal}
          onCancel={() => setTaxModal(false)}
          footer={null}
          width={600}
        >
          <Form
            form={taxForm}
            layout="vertical"
            onFinish={handleAddTax}
            autoComplete="off"
          >
            <Form.Item
              label="Tax Name"
              name="taxName"
              rules={[
                { required: true, message: "Please enter the tax name." },
              ]}
            >
              <Input placeholder="Enter tax name" />
            </Form.Item>
            <Form.Item
              label="Value (in %)"
              name="percentage"
              rules={[
                { required: true, message: "Please enter a valid percentage." },
                {
                  type: "number",
                  min: 0,
                  max: 100,
                  message: "Percentage should be between 0 and 100.",
                },
              ]}
            >
              <InputNumber
                placeholder="Enter tax percentage"
                min={0}
                max={100}
                step={0.01}
                precision={2}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" block htmlType="submit">
                Add Tax
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {showConfirmationModal && (
        <Modal
          title={
            <div
              style={{
                textAlign: "center",
                fontWeight: "bold",
                color: "#4a90e2",
              }}
            >
              <span style={{ fontSize: "16px" }}>CONFIRM PURCHASE UPDATE</span>{" "}
              <hr />
            </div>
          }
          open={showConfirmationModal}
          onCancel={() => {
            setShowConfirmationModal(false);
            setPurchaseFormData(null);
          }}
          footer={null}
          style={{
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
          }}
          width={500}
        >
          <div style={{ padding: "24px", textAlign: "center" }}>
            <div
              style={{
                fontSize: "16px",
                color: "#595959",
                marginBottom: "24px",
                lineHeight: "1.5",
              }}
            >
              Are you sure you want to update this purchase invoice?
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#8c8c8c",
                marginBottom: "24px",
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "6px",
              }}
            >
              This action will update the purchase record and cannot be undone.
            </div>
            <div
              style={{ display: "flex", gap: "12px", justifyContent: "center" }}
            >
              <Button
                onClick={() => {
                  setShowConfirmationModal(false);
                  setPurchaseFormData(null);
                }}
                style={{ minWidth: "100px" }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmPurchase}
                loading={isLoading}
                style={{ minWidth: "100px" }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Drawer>
  );
};

export default PurchaseDrawer;
